import React, { useEffect } from "react";

import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  useLocation,
} from "react-router-dom";

import AppLayout from "./components/AppLayout";
import AppointmentsPage from "./pages/Appointments/AppointmentsPage";
import { LoginPage } from "./pages/Auth";
import EmailInputPage from "./pages/Auth/ForgotPassword/EmailInput/EmailInputPage";
import ImpersonatePage from "./pages/Auth/ImpersonatePage";
import RegisterPage from "./pages/Auth/RegisterPage";
import RegisterSuccessPage from "./pages/Auth/RegisterPage/RegisterSuccessPage";
import ClientExercisePage from "./pages/Clients/ClientDetails/ClientDiary/Exercises/ClientExercisesPage";
import ClientDiaryPage from "./pages/Clients/ClientDetails/ClientDiary/Index/ClientDiaryPage";
import ClientDiaryMealPage from "./pages/Clients/ClientDetails/ClientDiary/Meals";
import WaterIntakePage from "./pages/Clients/ClientDetails/ClientDiary/WaterIntake/WaterIntakePage";
import ClientWeightLogsPage from "./pages/Clients/ClientDetails/ClientDiary/Weights/ClientWeightLogsPage";
import ClientModulePage from "./pages/Clients/ClientDetails/ClientModules/ClientModulePage";
import ClientModulesPage from "./pages/Clients/ClientDetails/ClientModules/ClientModulesPage";
import EditPlanPage from "./pages/Clients/ClientDetails/ClientPlan/Edit/EditPlanPage";
import ClientPlansPage from "./pages/Clients/ClientDetails/ClientPlan/Index/ClientPlansPage";
import NewPlanPage from "./pages/Clients/ClientDetails/ClientPlan/New/NewPlanPage";
import PlanListPage from "./pages/Clients/ClientDetails/ClientPlan/PlanList/PlanListPage";
import ViewPlanPDFPage from "./pages/Clients/ClientDetails/ClientPlan/ViewPlanPDF/ViewPlanPDFPage";
import ClientProfilePage from "./pages/Clients/ClientDetails/ClientProfile/ClientProfilePage";
import BodyCompositionHistoryTable from "./pages/Clients/ClientDetails/ClientProfile/components/Body-composition/components/BodyCompositionHistoryTable";
import BodyMeasurementHistoryTable from "./pages/Clients/ClientDetails/ClientProfile/components/Body-measurement/components/BodyMeasurementHistoryTable";
import ClientReportsPage from "./pages/Clients/ClientDetails/ClientReports/ClientReportsPage";
import ClientMealRatingsPage from "./pages/Clients/ClientDetails/ClientReports/MealRatings/ClientMealRatingsPage";
import ClientDetailsPage from "./pages/Clients/ClientDetails/Index/ClientDetailsPage";
import MessageSection from "./pages/Clients/ClientDetails/Index/components/MessageSection";
import ClientsPage from "./pages/Clients/ClientsPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import PendingAppInvitePage from "./pages/Dashboard/PendingAppInvitePage";
import DatabasePage from "./pages/Database/Index/DatabasePage";
import EditRecipePage from "./pages/Database/Recipes/Edit/EditRecipePage";
import RecipesPage from "./pages/Database/Recipes/Index/RecipesPage";
import CreateRecipePage from "./pages/Database/Recipes/New/CreateRecipePage";
import CreateFormPage from "./pages/Forms/CreateForm/CreateFormPage";
import EditFormPage from "./pages/Forms/EditForm/EditFormPage";
import FormsPage from "./pages/Forms/FormsPage";
import MessageChatPage from "./pages/Messages/MessageChat/MessageChatPage";
import MessageListPage from "./pages/Messages/MessageList/MessageListPage";
import NotificationPage from "./pages/Notification/NotificationPage";
import PaymentFailedPage from "./pages/PaymentFailed/Index/PaymentFailedPage";
import PaymentSuccessfulPage from "./pages/PaymentSuccessfulPage/Index/PaymentSuccessfulPage";
import Account from "./pages/Profile/components/Account";
import ChangePassword from "./pages/Profile/components/ChangePassword";
import EditPersonalInfo from "./pages/Profile/components/EditPersonalInfo";
import PersonalInfo from "./pages/Profile/components/PersonalInfo";
import TrainingsAndSpecialization from "./pages/Profile/components/TrainingsAndSpecialization";
import Profile from "./pages/Profile/Profile";
import SchedulerPage from "./pages/Scheduler/SchedulerPage";
import AccountDeletedPage from "./pages/SessionTimeout/AccountDeletedPage";
import SessionTimeoutPage from "./pages/SessionTimeout/SessionTimeoutPage";
import SubscriptionPage from "./pages/Subscription/Index/SubscriptionPage";
import EditMealPlanTemplatePage from "./pages/Templates/MealTemplates/Edit/EditMealPlanTemplatePage";
import MealTemplatesPage from "./pages/Templates/MealTemplates/Index/MealTemplatesPage";
import NewMealPlanTemplatePage from "./pages/Templates/MealTemplates/New/NewMealPlanTemplatePage";
import TemplatesRoutes from "./pages/Templates/Routes";
import { formatISODate } from "./utils/datetime";
import { RequireAuth, RequireNoAuth } from "./utils/routes";

const Routes: React.FC = () => {
  // Record page visits
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag("config", import.meta.env.VITE_GOOGLE_ANALYTICS_ID, {
        page_title: location.pathname,
        page_path: location.pathname,
      });
    }
  }, [location]);

  return (
    <ReactRoutes>
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/:tab" element={<DashboardPage />} />
        <Route path="messages" element={<MessageListPage />}>
          <Route path=":chatId/*" element={<MessageChatPage />} />
        </Route>
        <Route path="clients">
          <Route index element={<ClientsPage />} />
          <Route path="invites" element={<PendingAppInvitePage />} />
          <Route path=":clientId" element={<ClientDetailsPage />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ClientProfilePage />} />
            <Route
              path="profile/body-measurement-history"
              element={<BodyMeasurementHistoryTable />}
            />
            <Route
              path="profile/body-composition-history"
              element={<BodyCompositionHistoryTable />}
            />
            <Route path="diary">
              <Route
                index
                element={<Navigate to={formatISODate(new Date())} replace />}
              />
              <Route path=":date" element={<ClientDiaryPage />}>
                <Route index element={<Navigate to="meals" replace />} />
                <Route path="meals" element={<ClientDiaryMealPage />} />
                <Route path="exercises" element={<ClientExercisePage />} />
                <Route path="water-intakes" element={<WaterIntakePage />} />
                <Route path="weights" element={<ClientWeightLogsPage />} />
              </Route>
            </Route>
            <Route path="programs" element={<ClientPlansPage />}>
              <Route index element={<PlanListPage />} />
              <Route path="new" element={<NewPlanPage />} />
              <Route path=":planId/edit" element={<EditPlanPage />} />
              <Route path=":planId/pdf" element={<ViewPlanPDFPage />} />
            </Route>
            <Route path="messages/*" element={<MessageSection />} />
            <Route path="modules" element={<ClientModulesPage />} />
            <Route path="modules/:answerId" element={<ClientModulePage />} />
            <Route path="reports">
              <Route
                index
                element={<Navigate to={formatISODate(new Date())} replace />}
              />
              <Route path=":date" element={<ClientReportsPage />}>
                <Route index element={<Navigate to="meal-ratings" replace />} />
                <Route
                  path="meal-ratings"
                  element={<ClientMealRatingsPage />}
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="scheduler" element={<SchedulerPage />} />
        <Route path="templates" element={<TemplatesRoutes />}>
          <Route path="meals">
            <Route index element={<MealTemplatesPage />} />
            <Route path="new" element={<NewMealPlanTemplatePage />} />
            <Route
              path=":templateId/edit"
              element={<EditMealPlanTemplatePage />}
            />
          </Route>
        </Route>
        <Route path="database" element={<DatabasePage />}>
          <Route path="recipes">
            <Route index element={<RecipesPage />} />
            <Route path="new" element={<CreateRecipePage />} />
            <Route path=":recipeId/edit" element={<EditRecipePage />} />
          </Route>
        </Route>
        <Route path="forms">
          <Route index element={<FormsPage />} />
          <Route path="new" element={<CreateFormPage />} />
          <Route path=":id/edit" element={<EditFormPage />} />
        </Route>
        <Route path="notification" element={<NotificationPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="profile" element={<Profile />}>
          <Route path="personal-info" element={<PersonalInfo />} />
          <Route
            path="trainings-and-specialization"
            element={<TrainingsAndSpecialization />}
          />
          <Route path="edit-personal-info" element={<EditPersonalInfo />} />
          <Route path="account" element={<Account />} />
          <Route path="change-password" element={<ChangePassword />} />
        </Route>
      </Route>

      {/* Auth */}
      <Route
        path="login"
        element={
          <RequireNoAuth>
            <LoginPage />
          </RequireNoAuth>
        }
      />
      <Route
        path="register"
        element={
          <RequireNoAuth>
            <RegisterPage />
          </RequireNoAuth>
        }
      />
      <Route
        path="register-success"
        element={
          <RequireNoAuth>
            <RegisterSuccessPage />
          </RequireNoAuth>
        }
      />
      <Route
        path="forgot-password"
        element={
          <RequireNoAuth>
            <EmailInputPage />
          </RequireNoAuth>
        }
      />

      {/* Special pages */}
      <Route path="account-deleted" element={<AccountDeletedPage />} />
      <Route path="session-timeout" element={<SessionTimeoutPage />} />
      <Route path="payment-failed" element={<PaymentFailedPage />} />
      <Route path="payment-successful" element={<PaymentSuccessfulPage />} />
      <Route path="impersonate" element={<ImpersonatePage />} />

      {/* Catch all 404 not found page */}
      <Route path="404" element={<h1>Not Found</h1>} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </ReactRoutes>
  );
};

export default Routes;
